import { createSlice } from '@reduxjs/toolkit';
import { automixIncluded } from '../../utils/automixFormat';

// Prepare > Automix session state. The build lives here (not in the tab) so a
// ten-minute render keeps running — and keeps reporting — while the user is
// somewhere else in the app. Everything except `engine` names the signed-in
// account's playlists and files and is cleared by wipeUserScopedState.
//
// The running order, in one picture:
//   Best order ('flow')  — MixGoat decides. Until revealed the list is just the
//                          tracks (playlist order, no numbers); "Reveal the
//                          order" shows MixGoat's order in the list, numbered,
//                          with the pinned opener/closer at the ends. A build
//                          plays exactly that order.
//   My order ('playlist') — the list's order is the set's order: the playlist's
//                          own order plus whatever the user changed this
//                          session (`myOrder`). Dragging a track (in either
//                          mode) lands here; a reveal never overwrites it.
const initialState = {
    // What this machine can do: {available, reason, stemsAvailable, defaultOutDir}.
    // null until asked.
    engine: null,

    // The chosen playlist: {kind: 'cloud'|'local', id, name, cover}.
    source: null,
    // Its rows in display order: {key, id (library track id|null), pos (playlist position), path|null, title, artist, bpm, musicalKey, duration, cover}.
    tracks: [],
    // the rows themselves are on their way (nothing to show yet)
    tracksLoading: false,
    // the rows are on screen while their files are still being read — tempo,
    // key, length, whether the file is there (see tracksDescribed)
    describing: false,
    setName: '',
    // Seeded from the saved preference (automixSettings.order) when a playlist opens.
    orderMode: 'flow',
    // My order: the user's own running order this session (row keys), or null
    // = the playlist's order untouched. Survives switching to Best order and back.
    myOrder: null,
    // 'playlist' (untouched) | 'edited' (myOrder holds the user's changes)
    orderOrigin: 'playlist',
    // Best order only — the records the set must open / close with (row keys).
    openerKey: null,
    closerKey: null,

    // MixGoat's order as last revealed, and the inputs it was planned for
    // ({keys, sig}); shown only while `sig` still matches (see orderSignature).
    reveal: null,
    // 'idle' | 'running' | 'stopping'
    revealPhase: 'idle',
    revealProgress: null,
    revealError: null,

    // 'idle' | 'building' | 'stopping' | 'failed'
    phase: 'idle',
    // epoch ms the running build started (the build view's clock)
    startedAt: null,
    progress: null,
    // {code, message, details[]}
    error: null,

    // The set on screen (a fresh build or one opened from the shelf).
    set: null,
    // Finished sets in the output folder, newest first.
    sets: [],
    // a picture chosen on the setup screen, before the set exists:
    // {path} is what the build writes, {preview} is what the rail shows
    coverPick: null,
    setsLoaded: false,
    // "See all N" on the sets shelf. Session state, not a saved preference:
    // the shelf is left the way you left it when you open a set and come
    // back, or leave the tab and return.
    setsExpanded: false,
};

/** What a reveal depends on: which records are in the set, and the pinned ends. */
export function orderSignature(state) {
    const keys = automixIncluded(state.tracks).included.map((t) => t.key).sort();
    const inSet = new Set(keys);
    return JSON.stringify([keys, inSet.has(state.openerKey) ? state.openerKey : null, inSet.has(state.closerKey) ? state.closerKey : null]);
}

/** Best order is showing MixGoat's order (not just the pool of tracks). */
export function isRevealShown(state) {
    return state.orderMode === 'flow' && !!state.reveal && state.reveal.sig === orderSignature(state);
}

function reorder(state, keys) {
    const byKey = new Map(state.tracks.map((t) => [t.key, t]));
    const first = keys.filter((k) => byKey.has(k));
    const seen = new Set(first);
    state.tracks = [...first.map((k) => byKey.get(k)), ...state.tracks.filter((t) => !seen.has(t.key))];
}

const byPlaylistPos = (a, b) => (a.pos ?? 0) - (b.pos ?? 0);

// Best order shows MixGoat's order while the reveal still fits, else the pool.
function showFlow(state) {
    if (state.reveal && state.reveal.sig === orderSignature(state)) reorder(state, state.reveal.keys);
    else state.tracks.sort(byPlaylistPos);
}

// My order shows the user's order: the playlist's, plus this session's changes.
function showMine(state) {
    state.tracks.sort(byPlaylistPos);
    if (state.myOrder) reorder(state, state.myOrder);
}

function clearOrder(state) {
    state.myOrder = null;
    state.orderOrigin = 'playlist';
    state.openerKey = null;
    state.closerKey = null;
    state.reveal = null;
    state.revealPhase = 'idle';
    state.revealProgress = null;
    state.revealError = null;
}

const automixSlice = createSlice({
    name: 'automix',
    initialState,
    reducers: {
        setEngine(state, action) {
            state.engine = action.payload;
        },
        sourceLoading(state, action) {
            state.source = action.payload;
            state.tracks = [];
            state.tracksLoading = true;
            state.describing = false;
            state.setName = action.payload?.name || '';
            state.error = null;
            state.set = null;
            state.coverPick = null;
            if (state.phase === 'failed') state.phase = 'idle';
            clearOrder(state);
        },
        sourceLoaded(state, action) {
            state.tracks = action.payload?.tracks || [];
            state.tracksLoading = false;
            state.describing = !!action.payload?.describing;
            // a playlist with no cover of its own wears its first track's art
            if (action.payload?.cover && state.source) state.source.cover = action.payload.cover;
        },
        // The files behind the rows have been read: each row takes what its
        // file (or the library) told — `byKey[key]` is the patch, `{path: null}`
        // for a file that isn't there. Merged by key, so rows removed or moved
        // meanwhile are left as they are; the order stays.
        tracksDescribed(state, action) {
            const byKey = action.payload?.byKey || {};
            state.tracks = state.tracks.map((t) => (byKey[t.key] ? { ...t, ...byKey[t.key] } : t));
            state.describing = false;
        },
        clearSource(state) {
            state.source = null;
            state.tracks = [];
            state.tracksLoading = false;
            state.describing = false;
            state.setName = '';
            state.error = null;
            if (state.phase === 'failed') state.phase = 'idle';
            clearOrder(state);
        },
        // Leave rows out of this set (the playlist itself is untouched). Takes a
        // key or an array of keys (a multi-row selection).
        removeTrack(state, action) {
            const gone = new Set([].concat(action.payload));
            state.tracks = state.tracks.filter((t) => !gone.has(t.key));
            if (state.myOrder) state.myOrder = state.myOrder.filter((k) => !gone.has(k));
            if (gone.has(state.openerKey)) state.openerKey = null;
            if (gone.has(state.closerKey)) state.closerKey = null;
            if (state.orderMode === 'flow') showFlow(state);   // a changed set is a stale reveal
        },
        // Dragging a track makes the list's order the user's (My order), from
        // either mode: in Best order it starts from what the list showed.
        moveTrack(state, action) {
            const { key, toIndex } = action.payload;
            const from = state.tracks.findIndex((t) => t.key === key);
            if (from < 0) return;
            const [row] = state.tracks.splice(from, 1);
            state.tracks.splice(Math.max(0, Math.min(state.tracks.length, toIndex)), 0, row);
            state.myOrder = state.tracks.map((t) => t.key);
            state.orderMode = 'playlist';
            state.orderOrigin = 'edited';
        },
        // My order back to the playlist's own order.
        resetMyOrder(state) {
            state.myOrder = null;
            state.orderOrigin = 'playlist';
            if (state.orderMode === 'playlist') showMine(state);
        },
        setOrderMode(state, action) {
            const mode = action.payload === 'playlist' ? 'playlist' : 'flow';
            if (mode === state.orderMode) return;
            state.orderMode = mode;
            if (mode === 'playlist') showMine(state);
            else showFlow(state);
        },
        setOpenerKey(state, action) {
            state.openerKey = action.payload || null;
            if (state.openerKey && state.closerKey === state.openerKey) state.closerKey = null;
            if (state.orderMode === 'flow') showFlow(state);
        },
        setCloserKey(state, action) {
            state.closerKey = action.payload || null;
            if (state.closerKey && state.openerKey === state.closerKey) state.openerKey = null;
            if (state.orderMode === 'flow') showFlow(state);
        },
        revealStarted(state) {
            state.revealPhase = 'running';
            state.revealProgress = null;
            state.revealError = null;
        },
        revealProgressed(state, action) {
            if (state.revealPhase !== 'idle') state.revealProgress = action.payload;
        },
        revealStopping(state) {
            if (state.revealPhase === 'running') state.revealPhase = 'stopping';
        },
        // MixGoat's order arrived for these inputs (`sig`): Best order shows it.
        // (From My order too — the user's own order stays in myOrder.)
        revealApplied(state, action) {
            const { keys, sig } = action.payload;
            state.reveal = { keys, sig };
            state.orderMode = 'flow';
            showFlow(state);
            state.revealPhase = 'idle';
            state.revealProgress = null;
        },
        revealEnded(state) {
            state.revealPhase = 'idle';
            state.revealProgress = null;
        },
        revealFailed(state, action) {
            state.revealPhase = 'idle';
            state.revealProgress = null;
            state.revealError = action.payload;
        },
        dismissRevealError(state) {
            state.revealError = null;
        },
        setSetName(state, action) {
            state.setName = action.payload;
        },
        buildStarted(state, action) {
            state.phase = 'building';
            state.startedAt = action.payload || null;
            state.progress = null;
            state.error = null;
            state.set = null;
        },
        buildProgress(state, action) {
            // a late tick must not resurrect a build that already ended
            if (state.phase === 'building' || state.phase === 'stopping') state.progress = action.payload;
        },
        buildStopping(state) {
            if (state.phase === 'building') state.phase = 'stopping';
        },
        buildFinished(state, action) {
            state.phase = 'idle';
            state.progress = null;
            state.set = action.payload;
        },
        buildStopped(state) {
            state.phase = 'idle';
            state.progress = null;
        },
        buildFailed(state, action) {
            state.phase = 'failed';
            state.progress = null;
            state.error = action.payload;
        },
        dismissError(state) {
            state.error = null;
            if (state.phase === 'failed') state.phase = 'idle';
        },
        openSet(state, action) {
            state.set = action.payload;
        },
        closeSet(state) {
            state.set = null;
        },
        setCoverPick(state, action) {
            state.coverPick = action.payload;   // null clears it
        },
        setSetsExpanded(state, action) {
            state.setsExpanded = !!action.payload;
        },
        setSets(state, action) {
            state.sets = action.payload || [];
            state.setsLoaded = true;
        },
        resetAutomix(state) {
            return { ...initialState, engine: state.engine };
        },
    },
});

export const {
    setEngine, sourceLoading, sourceLoaded, tracksDescribed, clearSource, removeTrack, moveTrack, resetMyOrder, setSetName,
    setOrderMode, setOpenerKey, setCloserKey,
    revealStarted, revealProgressed, revealStopping, revealApplied, revealEnded, revealFailed,
    dismissRevealError,
    buildStarted, buildProgress, buildStopping, buildFinished, buildStopped, buildFailed,
    dismissError, openSet, closeSet, setSets, setSetsExpanded, setCoverPick, resetAutomix,
} = automixSlice.actions;
export default automixSlice.reducer;
